import React, { useEffect, useRef, useState } from 'react';
import GearsBackground from '../components/GearsBackground';

const SECTION_IDS = ['home', 'about', 'resume', 'portfolio', 'testimonials', 'contact'];
const STATUS_TICKER_ITEMS = [
  'GATEWAY NETWORK — ONLINE',
  'EAM — OPERATIONAL',
  'REDUNDANCY — ACTIVE',
  'THREAD POOL — STABLE',
  'PROTOCOL BUFFERS — SYNCED',
  'BUILD — PASSING',
];

function Header({ data }) {
  const [navOpen, setNavOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');

  const sectionsRef = useRef([]);
  const navHeightRef = useRef(72);
  const suppressUntilRef = useRef(0);

  const refreshSections = () => {
    sectionsRef.current = SECTION_IDS.map((id) => document.getElementById(id)).filter(Boolean);
  };

  const smoothScrollTo = (targetY, duration = 350) => {
    const startY = window.scrollY;
    const delta = targetY - startY;
    const start = performance.now();

    const step = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = t * (2 - t); // easeOutQuad
      window.scrollTo(0, startY + delta * eased);
      if (t < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  };

  const onNavClick = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;

    setActiveSection(id);
    setNavOpen(false);

    // suppress scroll-driven updates during animation
    suppressUntilRef.current = performance.now() + 450;

    // use current nav height (handles shrink)
    const navEl = document.getElementById('nav-wrap');
    navHeightRef.current = navEl?.offsetHeight || navHeightRef.current || 72;

    // scroll to element with fixed-nav offset
    const targetY = el.getBoundingClientRect().top + window.scrollY - navHeightRef.current;
    smoothScrollTo(targetY, 350);
  };

  const updateActiveSection = () => {
    if (!sectionsRef.current.length || sectionsRef.current.length < SECTION_IDS.length) {
      refreshSections();
    }
    if (!sectionsRef.current.length) return;
    if (performance.now() < suppressUntilRef.current) return;

    const navEl = document.getElementById('nav-wrap');
    navHeightRef.current = navEl?.offsetHeight || navHeightRef.current || 72;

    const anchorY = window.scrollY + navHeightRef.current + 8;

    let activeId = sectionsRef.current[0].id;
    for (const sec of sectionsRef.current) {
      const rect = sec.getBoundingClientRect();
      const top = rect.top + window.scrollY;
      const padTop = Math.min(parseFloat(getComputedStyle(sec).paddingTop) || 0, 80);
      const visualTop = top + padTop;
      const visualBottom = top + rect.height;
      if (anchorY >= visualTop && anchorY < visualBottom) {
        activeId = sec.id;
        break;
      }
    }
    setActiveSection((prev) => (prev !== activeId ? activeId : prev));
  };

  useEffect(() => {
    const handleScroll = () => {
      const nav = document.getElementById('nav-wrap');
      if (nav) nav.classList.toggle('shrink', window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });

    const onHashChange = () => {
      const id = (location.hash || '#home').slice(1) || 'home';
      setActiveSection((prev) => (prev !== id ? id : prev));
    };
    window.addEventListener('hashchange', onHashChange, { passive: true });

    // initial nav height
    navHeightRef.current = document.getElementById('nav-wrap')?.offsetHeight || 72;

    // cache sections now and on next frame
    refreshSections();
    const raf = requestAnimationFrame(refreshSections);

    // start tracking
    updateActiveSection();
    window.addEventListener('scroll', updateActiveSection, { passive: true });

    const onResize = () => {
      navHeightRef.current = document.getElementById('nav-wrap')?.offsetHeight || 72;
      refreshSections();
      updateActiveSection();
    };
    window.addEventListener('resize', onResize, { passive: true });

    // proximity glow (desktop only)
    const onMouseMove = (e) => {
      const header = document.getElementById('home');
      if (!header) return;

      const rect = header.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;

      const dx = e.clientX - cx;
      const dy = e.clientY - cy;
      const distance = Math.sqrt(dx * dx + dy * dy);

      const ACTIVE_RADIUS = 220;
      if (distance < ACTIVE_RADIUS) header.classList.add('gear-proximity');
      else header.classList.remove('gear-proximity');
    };
    window.addEventListener('mousemove', onMouseMove, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('scroll', updateActiveSection);
      window.removeEventListener('hashchange', onHashChange);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('mousemove', onMouseMove);
      cancelAnimationFrame(raf);
    };
  }, []);

  const toggleNav = () => setNavOpen((prev) => !prev);

  let networks, tagline;
  if (data) {
    const { social, tagline: t } = data;
    tagline = t;
    networks = (social || []).map((network) => (
      <li key={network.name}>
        <a href={network.url} target="_blank" rel="noopener noreferrer">
          <i className={network.className}></i>
        </a>
      </li>
    ));
  }

  return (
    <header id="home">
      {/* soot/industrial overlay */}
      <div className="hero-overlay" />

      <GearsBackground />

      <nav id="nav-wrap" className={navOpen ? 'open' : ''}>
        <div id="nav-inner">
          <a
            id="equation-logo"
            href="/"
            onClick={(e) => { e.preventDefault(); window.location.reload(); }}
          >
            𝑎⁽ˡ⁾ = σ(𝑊⁽ˡ⁾𝑎⁽ˡ⁻¹⁾ + 𝑏⁽ˡ⁾)
          </a>

          <button
            className="mobile-toggle"
            aria-label="Toggle navigation"
            aria-expanded={navOpen}
            onClick={toggleNav}
          >
            <span />
            <span />
            <span />
          </button>

          <ul id="nav" className="nav">
            <li data-active={activeSection === 'home'}>
              <a className="smoothscroll" href="#home" onClick={(e) => onNavClick(e, 'home')}>Home</a>
            </li>
            <li data-active={activeSection === 'about'}>
              <a className="smoothscroll" href="#about" onClick={(e) => onNavClick(e, 'about')}>About</a>
            </li>
            <li data-active={activeSection === 'resume'}>
              <a className="smoothscroll" href="#resume" onClick={(e) => onNavClick(e, 'resume')}>Resume</a>
            </li>
            <li data-active={activeSection === 'portfolio'}>
              <a className="smoothscroll" href="#portfolio" onClick={(e) => onNavClick(e, 'portfolio')}>Works</a>
            </li>
            <li data-active={activeSection === 'testimonials'}>
              <a className="smoothscroll" href="#testimonials" onClick={(e) => onNavClick(e, 'testimonials')}>Quotes</a>
            </li>
            <li data-active={activeSection === 'contact'}>
              <a className="smoothscroll" href="#contact" onClick={(e) => onNavClick(e, 'contact')}>Contact</a>
            </li>
          </ul>

          <div className="status-badge" aria-hidden="true">
            <span className="status-led" />
            System Online
          </div>
        </div>

        <div className="hmi-ticker" aria-hidden="true">
          <div className="hmi-ticker-track">
            {[...STATUS_TICKER_ITEMS, ...STATUS_TICKER_ITEMS].map((item, i) => (
              <span className="hmi-ticker-item" key={i}>
                {item}
                <span className="hmi-ticker-sep">●</span>
              </span>
            ))}
          </div>
        </div>
      </nav>

      <div className="row banner">
        <div className="banner-text">
          <h1 className="responsive-headline fade-in-up hero-title">
            <span className="responsive-headline power-on">Nicholas Lewis</span>
          </h1>

          <h3 className="fade-in-up hero-subtitle">
            {tagline || 'Software Engineer · SCADA & Industrial Automation · Distributed Systems · Java'}
          </h3>

          <hr />
          <ul className="social fade-in-up">{networks}</ul>
        </div>
      </div>

      <p className="scrolldown">
        <a className="smoothscroll" href="#about"><i className="icon-down-circle"></i></a>
      </p>
    </header>
  );
}

export default Header;
